import { Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { LargePlantStats, PlantStats } from '../shared/models/large-scale-plant';
import { NgxSpinnerModule } from 'ngx-spinner';
import { sensorRows, SiteInfo, siteInfoRows, TabType, meterHeaderLabels } from '../shared/models/enum';
import { CommonModule } from '@angular/common';
import { ProductService } from '../shared/service/product.service';
import { SiteInfoData, SiteInfoResponse } from '../shared/models/site-info-response';
import { Sensor, SensorApiResponse } from '../shared/models/sensors-response';
import { MeterData, MetersResponse } from '../shared/models/meters-response';
import { InverterStringResponse } from '../shared/models/inverter-string-response';
import { BaseComponent } from '../base/components/base.component';
import { CapitalizePipe } from '../shared/pipe/capitilize.pipe';
import { LargeScalePowerGenerationComponent } from "../large-scale-power-generation/large-scale-power-generation.component";
import { LargeScaleTodayGenerationComponent } from "../large-scale-today-generation/large-scale-today-generation.component";
import { LargeScalePlantGenerationComponent } from "../large-scale-plant-generation/large-scale-plant-generation.component";
import { PufComponent } from "../large-scale-cr/puf/puf.component";
import { LargePlant, LargePlantsResponse } from '../shared/models/large-plant-details';
import { interval, startWith, Subject, takeUntil } from 'rxjs';
import moment from 'moment';
@Component({
    selector: 'app-large-scale-plant',
    imports: [TranslateModule,
        NgxSpinnerModule,
        CommonModule,
        CapitalizePipe, LargeScalePowerGenerationComponent, LargeScaleTodayGenerationComponent, LargeScalePlantGenerationComponent, PufComponent],
    templateUrl: './large-scale-plant.component.html',
    styleUrl: './large-scale-plant.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LargeScalePlantComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild(LargeScalePlantGenerationComponent) child1!: LargeScalePlantGenerationComponent;
  @ViewChild(LargeScalePowerGenerationComponent) child2!: LargeScalePowerGenerationComponent;
  @ViewChild(LargeScaleTodayGenerationComponent) child3!: LargeScaleTodayGenerationComponent;
  @ViewChild(PufComponent) child4!: PufComponent; 
  TTL = 5 * 60 * 1000;
  cdr = inject(ChangeDetectorRef)
  largeScalePlantStats!: PlantStats;
  selectedTab: TabType = TabType.INVERTER_STRING;
  TabType = TabType;
  inverterColumns: string[] = [];
  inverterTableData: any[] = [];
  meterKeys!: string[];
  sensorsData: any[] = [];
  siteInfoRows: { label: string; value: any }[] = [];
  productService = inject(ProductService)
  meterMainKeys!: string[];
  meterGridMeter!: string[];
  meterDG1Value!: string[];
  meterSolarMeter!: string[];
  meterRows!: any;
  sensorKeys!: string[];
  sensorRows!: any;
  siteInfo!: SiteInfoData | undefined;
  siteInfoItems: SiteInfo[] = siteInfoRows;
  sensorItems = sensorRows;
  meterItems = meterHeaderLabels;
  inverterStringData: any;
  inverters: Record<string, Record<string, number>> = {};
  inverterKeys: string[] = [];
  stringKeys: string[] = [];
  plantDetails: LargePlant[] = [];
  selectedPlant?: LargePlant;
  plantId!: string;
  sensors: Sensor[] = [];
  readonly destroy$ = new Subject<void>();


  ngOnInit() {

    this.getLargePlantDetails();


  }

  startDashboardRefresh() {
    interval(this.TTL)
      .pipe(
        startWith(0),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.getLargePlantStatsData();
        this.child1?.getPlantGeneration();
        this.child2?.getPowerGeneration();
        this.child3?.getTodayGeneration();
        this.child4?.getCfPufGeneration();
        this.cdr.detectChanges();
      });
    
    setTimeout(() => {
      this.getLargePlantInverterStringsData(true);
    }, 500);
    
    setTimeout(() => {
      this.getLargePlantMetersData(true);
      this.getLargePlantSensorsData(true);
      this.getLargePlantSiteInfoData(true);
      this.cdr.detectChanges();
    }, 900);
  }

  getLargePlantDetails() {
    this.productService.getLargePlantDetails().subscribe({
      next: (response: LargePlantsResponse) => {
        this.plantDetails = response.data?.largePlantsList ?? [];
        if (this.plantDetails.length > 0) {
          this.selectedPlant = this.plantDetails[0];
          this.plantId = this.selectedPlant.plantId;
          this.cdr.detectChanges();          
          this.startDashboardRefresh();
          this.startAutoRefresh();
        }
      },
      error: (err) => {
        this.logger.error(err);
      }
    });
  }
  getLargePlantStatsData() {
    if (!this.plantId) return
    this.productService.getLargePlantsStats(this.plantId).subscribe({
      next: (LargePlantStatsResponse: LargePlantStats) => {
        this.largeScalePlantStats = LargePlantStatsResponse.data
        this.cdr.detectChanges();
      }, error: (err) => {
        this.logger.error(err)

      },
    });

  }

  getLargePlantInverterStringsData(forceRefresh = false) {
    if (!this.plantId) return
    this.productService
      .getInverterStringData(this.plantId, forceRefresh)
      .subscribe({
        next: (response: InverterStringResponse) => {

          this.inverters = response.data.inverters || {};
          this.inverterKeys = Object.keys(this.inverters);
          let maxStrings = 0;
          this.inverterKeys.forEach(inv => {
            const count = Object.keys(this.inverters[inv] || {}).length;
            maxStrings = Math.max(maxStrings, count);
          });

          this.stringKeys = Array.from(
            { length: maxStrings },
            (_, i) => `String_${String(i + 1).padStart(2, '0')}`
          );
          this.cdr.detectChanges();
        },
        error: err => {
          this.logger.error(err);
        }
      });
  }

  getStringValue(inverter: string, stringName: string): number | '-' {
    return this.inverters?.[inverter]?.[stringName] ?? '-';
  }
  formatInverterName(value: string): string {
    if (!value) return '';

    return value
      .replaceAll('_', ' ')
      .replaceAll(/([a-zA-Z])(\d)/g, '$1 $2');
  }

  getLargePlantMetersData(forceRefresh = false) {
    if (!this.plantId) return;
    this.productService.getMetersData(this.plantId, forceRefresh).subscribe({
      next: (response: MetersResponse) => {
        const data = response.data;
        const keys = Object.keys(data.DG1Meter);
        this.meterKeys = [
          'parameter',
          ...keys.filter(k => k !== 'parameter')
        ];

        this.meterRows = [
          this.mapMeterRow(data.DG1Meter),
          this.mapMeterRow(data.GridMeter),
          this.mapMeterRow(data.SolarMeter)
        ];
        this.cdr.detectChanges();
      },
      error: err => this.logger.error(err)
    });
  }
  mapMeterRow(meter: MeterData) {
    return this.meterKeys.map(key => (meter as any)[key]);
  }
  getLargePlantSensorsData(forceRefresh = false) {
    if (!this.plantId) return;
    this.productService.getSensorsData(this.plantId, forceRefresh).subscribe({
      next: (response: SensorApiResponse) => {
        this.sensors = response.data.sensors;
        this.cdr.detectChanges();
      },
      error: err => this.logger.error(err)
    });
  }
  getLargePlantSiteInfoData(forceRefresh = false) {
    if (!this.plantId) return;
    this.productService.getSiteInfoData(this.plantId, forceRefresh).subscribe({
      next: (response: SiteInfoResponse) => {
        this.siteInfo = response.data;
        this.cdr.detectChanges();
      },
      error: err => this.logger.error(err)
    });
  }



  buttonSelected(tab: TabType) {
    this.selectedTab = tab;

    switch (tab) {
      case TabType.INVERTER_STRING: {
        const force = this.productService.isInverterExpiredOnRevisit(this.plantId);
        this.productService.markInverterVisited();
        this.getLargePlantInverterStringsData(force);
        break;
      }

      case TabType.METERS: {
        const force = this.productService.isMetersExpiredOnRevisit(this.plantId);
        this.productService.markMetersVisited();
        this.getLargePlantMetersData(force);
        break;
      }

      case TabType.SENSORS: {
        const force = this.productService.isSensorsExpiredOnRevisit(this.plantId);
        this.productService.markSensorsVisited();
        this.getLargePlantSensorsData(force);
        break;
      }

      case TabType.SITE_INFO: {
        const force = this.productService.isSiteInfoExpiredOnRevisit(this.plantId);
        this.productService.markSiteInfoVisited();
        this.getLargePlantSiteInfoData(force);
        break;
      }
    }
  }
  startAutoRefresh() {
    interval(5 * 60 * 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        switch (this.selectedTab) {
          case TabType.INVERTER_STRING:
            this.getLargePlantInverterStringsData(true);
            break;

          case TabType.METERS:
            this.getLargePlantMetersData(true);
            break;

          case TabType.SENSORS:
            this.getLargePlantSensorsData(true);
            break;

          case TabType.SITE_INFO:
            this.getLargePlantSiteInfoData(true);
            break;
        }
      });
  }

  dateFormatter(timestamp: number | null | undefined) {
    if (timestamp == null) {
      return '-';
    }
    const tsMs = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    return moment(tsMs).format('YYYY-MM-DD HH:mm:ss ');
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
